/* eslint-disable jsx-a11y/no-autofocus */
import {
  Dropdown,
  type DropdownLayoutValueOption,
  FormField,
  listItemSelectBuilder,
  type PopoverProps,
} from '@wix/design-system';
import React, { useMemo } from 'react';

import type { TFunction } from './../../../ricos-types/commonTypes';
import { useBiNext } from './../../bi-context-provider/bi-context-provider';
import { useSitePagesFetchContext } from './../../site-pages/site-pages-fetch-context';
import { isDefined } from './../../../wix-rich-content-common/index';

import { LINK_MODAL_ID } from '../constants';
import { LINK_TYPE } from '../data-hooks/data-hooks';
import type { LinkType } from './types';

type Props = {
  autoFocus: boolean;
  dropDownPopoverProps?: PopoverProps;
  onSelect: (type: LinkType) => void;
  showSitePages: boolean;
  t: TFunction;
  type: string;
};

type Option = DropdownLayoutValueOption & { id: LinkType; size: string };

const translationTypes: Record<LinkType, string> = {
  blogPost: 'BlogPost',
  section: 'Section',
  staticPage: 'StaticPage',
  'web-address': 'WebAddress',
};

const configToOption = (t: TFunction, linkType: LinkType) =>
  listItemSelectBuilder({
    id: linkType,
    label: t(`LinkSettings_TypeSelect_Option_${translationTypes[linkType]}`),
    size: 'small',
    title: t(`LinkSettings_TypeSelect_Option_${translationTypes[linkType]}`),
  }) as Option;

const defaultOptions: LinkType[] = ['web-address', 'section'];

export const TypeSelect: React.FC<Props> = ({
  autoFocus,
  dropDownPopoverProps,
  onSelect,
  showSitePages,
  t,
  type,
}) => {
  const logBi = useBiNext({ pluginId: 'link', modalId: LINK_MODAL_ID });

  const staticPagesState = useSitePagesFetchContext('staticPage', '').fetchState;
  const blogPostsState = useSitePagesFetchContext('blogPost', '').fetchState;

  const options = useMemo(() => {
    const availableLinkTypes: LinkType[] = [
      ...defaultOptions,
      staticPagesState && staticPagesState.type === 'ready' && staticPagesState.items?.length > 0
        ? ('staticPage' as const)
        : undefined,
      blogPostsState && blogPostsState.type === 'ready' && blogPostsState.items?.length > 0
        ? ('blogPost' as const)
        : undefined,
    ].filter(isDefined);
    return availableLinkTypes.map(linkType => configToOption(t, linkType));
  }, [showSitePages, t, staticPagesState?.type, blogPostsState?.type]);

  const onOptionsShow = () => {
    logBi({ dataHook: LINK_TYPE });
  };

  const onSelectOption = (option: DropdownLayoutValueOption) => {
    onSelect((option as Option).id);
    logBi({ dataHook: LINK_TYPE, value: (option as Option).id });
  };

  return (
    <FormField label={t('LinkSettings_TypeSelect_Label')} labelPlacement="top">
      <Dropdown
        autoFocus={autoFocus}
        dataHook={LINK_TYPE}
        onOptionsShow={onOptionsShow}
        onSelect={onSelectOption}
        options={options}
        popoverProps={dropDownPopoverProps}
        selectedId={type}
        size="small"
        valueParser={option => option.label}
      />
    </FormField>
  );
};
